import { normalizeStorageError, startUpload, UploadFileInput, UploadProgress, UploadTask } from '@Utils/S3Storage'
import { v4 as uuidv4 } from 'uuid'
import toastr from 'toastr'

import FileNameSanitizer from '@Utils/FileNameSanitizer'

import 'toastr/build/toastr.css'

const TOASTR_OPTIONS = {
  closeButton: true,
  timeOut: 0,
  extendedTimeOut: 0
}

export interface ITrainingSampleUploaderOptions {
  readonly speakerVoiceId: string
  uploadCallback: (params: IUploadedTrainingSample) => void
  showProgressCallback: (data: UploadProgress) => void
}

export interface IUploadedTrainingSample {
  readonly url: string
}

interface ITrainingSampleMetaData {
  readonly size: number
  readonly duration: number
  readonly filename: string
  readonly loaded: boolean
}

class TrainingSampleUploader {
  speakerVoiceId: string
  filename: string
  sanitizedFileName: string
  element: HTMLAudioElement
  audioMetaData: ITrainingSampleMetaData
  uploadCallback: (params: IUploadedTrainingSample) => void
  showProgressCallback: (data: UploadProgress) => void

  constructor(
    private readonly file: File,
    options: ITrainingSampleUploaderOptions,
  ) {
    this.file = file
    this.audioMetaData = { size: 0, duration: 0, filename: '', loaded: false }
    this.filename = decodeURI(encodeURI(this.file.name))
    // we have to take only the first 50 chars to suppress the error for tempfiles:
    // Filename too long @rb_sysopen - /tmp/...
    // the maximum tempfile's name is 170 chars, but we also add a UUID to it.
    this.sanitizedFileName = new FileNameSanitizer(this.filename).call().substring(0,50)
    this.uploadCallback = options.uploadCallback
    this.showProgressCallback = options.showProgressCallback
    this.element = this._initializeTrainingSample()
    this.speakerVoiceId = options.speakerVoiceId
  }

  call(): UploadTask {
    const params = this._generateAwsParams()
    return this._upload(params)
  }

  _upload(params: UploadFileInput): UploadTask {
    const { completion, task } = startUpload(params, this.showProgressCallback)

    void completion
      .then(url => {
        if (this.audioMetaData.loaded) {
          this.uploadCallback({ url })
        } else {
          toastr.error('There is a problem with the format of your file. Your file could be corrupted. Please check it.', 'Upload cannot be completed!', TOASTR_OPTIONS)
        }
        this.element.removeEventListener('loadedmetadata', () => {
          this.audioMetaData = {
            size: this.file.size,
            duration: this.element.duration,
            filename: this.filename,
            loaded: true
          }
        })
      })
      .catch((error: unknown) => {
        const uploadError = normalizeStorageError(error)

        if (uploadError.name === 'RequestTimeTooSkewed') {
          toastr.error('Please adjust the system time of your device. Then try the uploading again.', 'TrainingSample cannot be uploaded!', TOASTR_OPTIONS)
        }
        if (!window.Rollbar) { return }
        window.Rollbar.error(uploadError)
      })

    return task
  }

  _generateAwsParams(): UploadFileInput {
    const fileExtension = this.filename.split('.').pop()
    const bucketName = window.EditorSources ? `${window.EditorSources.s3Bucket}/speaker_voice/${this.speakerVoiceId}/training_files` : `${window.Checksub.s3Bucket}/speaker_voice/${this.speakerVoiceId}/training_files`

    // Characters That Might Require Special Handling
    // https://docs.aws.amazon.com/en_us/AmazonS3/latest/dev/UsingMetadata.html
    const key = encodeURI(`${this.sanitizedFileName}_${uuidv4()}.${fileExtension}`)

    return {
      body: this.file,
      bucket: bucketName,
      contentType: this.file.type,
      key,
    }
  }

  _initializeTrainingSample(): HTMLAudioElement {
    const element = document.createElement('audio')
    element.preload = 'metadata'
    element.src = URL.createObjectURL(this.file)

    element.addEventListener('loadedmetadata', () => {
      this.audioMetaData = {
        size: this.file.size,
        duration: this.element.duration,
        filename: this.filename,
        loaded: true
      }
    })

    return element
  }
}

export default TrainingSampleUploader
